import React, { useEffect, useState } from 'react';
import {
  Box,
  FormInput,
  FormInputColumn,
  Cluster,
  constants,
  LineItem,
  BoxWithShadow,
  CollapsibleSection,
  Motion,
  Title,
  SolidDivider,
  Detail,
  ButtonWithAction,
  Checkbox,
  Text,
  util,
  ButtonWithLink,
} from '@thecb/components';
import { required } from 'redux-freeform';
import * as R from 'ramda';
import { pdf } from '@react-pdf/renderer';
import { CloseoutReportDocument } from '../../components/pdf-reports';
import {
  reducer,
  mapStateToProps,
  mapDispatchToProps,
} from './CloseoutForm.state';
import {
  formatCurrency,
  formatPrice,
  downloadReport,
  getImageOrientation,
} from '../../utils/helpers';
import { noop } from 'rxjs';
import { OutOfBalanceModal, OUT_OF_BALANCE } from './CloseoutModals';
import { DynamicDenominationsFieldGroup } from '../../components/form-blocks';
import { useDispatch, useSelector } from 'react-redux';
import { LOGOUT_REASONS, OKTA_LOGOUT_REASON_KEY } from '../../utils/constants';
import { useNavigate } from 'react-router-dom';
import { openCashDrawer } from '../../state/peripheralDevices';
import fallbackLogo from '../../static/img/cityville-logo-blue.png';

const requireErrors = (error_text) => ({
  [required.error]: error_text,
});

export const CloseoutForm = ({
  actions,
  fields,
  forms,
  modals,
  submitCloseoutForm,
  openModal,
  toggleModal,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errors, handleErrors] = useState(false);
  const [isCashSectionOpen, setCashSectionIsOpen] = useState(false);
  const [denominationsFormState, setDenominationsFormState] = useState({});
  const { denominations } = useSelector((state) => state.shared);
  const { uuid, created_at, terminal } = useSelector(
    (state) => state.terminalLogin.terminalSession,
  );
  const {
    float,
    total_cash: expectedCashTotal,
    total_check: totalCheck,
    total_processed_internally_check: totalProcessedInternallyCheck,
    total_processed_externally_check: totalProcessedExternallyCheck,
    total_card: totalCard,
    total_wire_payment: totalWirePayment,
  } = useSelector((state) => state.closeout.data.batch);
  const { order_items } = useSelector((state) => state.cart.order);
  const { isLoading, data } = useSelector((state) => state.closeout);
  const {
    clientMetadata: { data: metadata },
  } = useSelector((state) => state.global);
  const batchOpenDate = new Date(created_at);
  const batchOpenDisplay = `${batchOpenDate.toLocaleDateString()} - ${batchOpenDate.toLocaleTimeString()}`;
  const handleLogout = () => {
    localStorage.setItem(OKTA_LOGOUT_REASON_KEY, LOGOUT_REASONS.CLOSEOUT.name);
    navigate('/logout');
  };
  useEffect(() => {
    actions.fields.float.set(formatCurrency(float));
  }, [float]);
  useEffect(() => {
    return () => actions.form.clear();
  }, []);

  const handleInputClick = (event) => {
    event.stopPropagation();
  };

  const denominationsSum = isCashSectionOpen
    ? Object.entries(denominationsFormState).reduce((acc, cur) => {
        const [key, quantity] = cur;
        return (acc +=
          quantity * denominations.find((d) => d.name === key).value || 0);
      }, 0)
    : parseFloat(fields.cashTotal.rawValue || 0);

  const closeoutTotal =
    denominationsSum +
    parseFloat(totalCheck) +
    parseFloat(totalCard) +
    parseFloat(totalWirePayment);

  const confirmAmountsBoolean = Boolean(fields.confirmAmounts.rawValue);
  const handleConfirmAmountsCheckboxClick = () => {
    actions.fields.confirmAmounts.set(!confirmAmountsBoolean);
  };

  const closeoutIsOutOfBalance = () => denominationsSum != expectedCashTotal;

  useEffect(() => {
    const lazyLoadPdf = async () => {
      const portraitOrLandscapeLogo = await getImageOrientation(
        metadata?.headerLogoLink ?? fallbackLogo, // in case FCS is not running locally
      );
      // from https://github.com/diegomura/react-pdf/issues/975
      const doc = (
        <CloseoutReportDocument
          {...data.reports}
          headerImg={metadata?.headerLogoLink}
          portraitOrLandscapeLogo={portraitOrLandscapeLogo}
        />
      );
      const asPdf = pdf();
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();
      const objectUrl = window.URL.createObjectURL(blob);
      downloadReport('Closeout', terminal, new Date(), objectUrl);
      handleLogout();
    };
    if (Object.keys(data.reports).length > 0) {
      lazyLoadPdf();
    }
  }, [data.reports]);

  const handleSubmit = () => {
    const hasDenominations = denominationsFormState && !!denominations.length;
    const reducedDenominationFields = Object.entries(
      denominationsFormState,
    ).reduce((acc, [key, quantity]) => {
      const denomination_id = denominations.find((d) => d.name === key).id;
      return quantity > 0 ? [...acc, { denomination_id, quantity }] : acc;
    }, []);
    const denominationsWithValues = hasDenominations
      ? reducedDenominationFields
      : [];
    return submitCloseoutForm({
      closing_float: fields.float.rawValue,
      cash_total: denominationsSum,
      closing_note: fields.notes.rawValue,
      denominations: denominationsWithValues,
    });
  };
  const handleCloseoutSubmitClick = util.general.generateClickHandler(
    forms.closeoutForm,
    () => handleErrors(true),
    closeoutIsOutOfBalance() ? () => openModal(OUT_OF_BALANCE) : handleSubmit,
  );

  const outOfBalanceModal = (
    <OutOfBalanceModal
      modals={modals}
      submit={handleSubmit}
      toggle={toggleModal}
    />
  );

  const cashDenominationsDropdownHeading = (
    <Box height="10rem" padding="0 0 0 1.5rem">
      <Cluster justify="space-between" align="center">
        <Box padding="0">
          <Cluster
            nowrap="true"
            overflow="visible"
            childGap="1rem"
            justify="flex-start"
          >
            <Detail margin="" as="h4">
              Cash
            </Detail>
            <Detail margin="" as="span">
              |
            </Detail>
            <ButtonWithAction
              variant="smallGhost"
              action={noop}
              text={
                isCashSectionOpen ? 'Hide Denominations' : 'Show Denominations'
              }
            />
          </Cluster>
        </Box>
        <Box padding="0">
          {isCashSectionOpen ? (
            <Cluster align="center" minHeight="5rem">
              <ButtonWithAction
                variant="smallGhost"
                action={(e) => {
                  e.stopPropagation();
                  setDenominationsFormState({});
                  actions.form.clear();
                }}
                text="Clear count"
              />
              <Detail>{formatPrice(denominationsSum)}</Detail>
            </Cluster>
          ) : (
            <Cluster overflow="visible">
              <FormInput
                data-testid="cash-total-input"
                extraStyles={`text-align: right; width: 9.5rem; margin-right: .5rem;`}
                onClick={handleInputClick}
                labelTextWhenNoError=""
                errorMessages={requireErrors('Cash Total Error')}
                field={fields.cashTotal}
                errors={fields.errors}
                fieldActions={actions.fields.cashTotal}
                labelTextVariant="p"
                errorTextVariant="p"
              />
            </Cluster>
          )}
        </Box>
      </Cluster>
    </Box>
  );

  return (
    <Box padding="0">
      {outOfBalanceModal}
      <Title as="h3" margin="0.25rem 0">
        Batch Details
      </Title>
      <BoxWithShadow
        variant="baseStandard"
        borderRadius=".25rem"
        padding="1.5rem"
        background={constants.colors.WHITE}
      >
        <Cluster justify="space-between" align="center">
          <Detail as="h5" margin="0 0 .25rem">
            Open date
          </Detail>
          <Detail weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}>
            {batchOpenDisplay}
          </Detail>
        </Cluster>
        <Cluster justify="space-between" align="center">
          <Detail as="h5" margin=".25rem 0">
            Batch number
          </Detail>
          <Detail weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}>
            {uuid}
          </Detail>
        </Cluster>
        <Cluster justify="space-between" align="center" overflow="visible">
          <Detail as="h5" margin=".25rem 0">
            Float
          </Detail>
          <FormInput
            isNum="true"
            extraStyles={`text-align: right; width: 9.5rem; margin-right: .25rem;`}
            data-testid="cash-float"
            labelTextWhenNoError=""
            field={fields.float}
            fieldActions={actions.fields.float}
            themeValues={{ lineHeight: 0 }}
          />
        </Cluster>
        <FormInput
          data-testid="batch-notes"
          labelTextWhenNoError="Batch notes (optional)"
          field={fields.notes}
          errors={fields.errors}
          fieldActions={actions.fields.notes}
          labelTextVariant="p"
          errorTextVariant="p"
        />
      </BoxWithShadow>
      <Title as="h3" margin="1.5rem 0 .25rem">
        Batch Totals
      </Title>
      <BoxWithShadow
        variant="baseStandard"
        borderRadius=".25rem"
        padding="0"
        background={constants.colors.WHITE}
      >
        <Box padding="0">
          <CollapsibleSection
            supportsTouch={false}
            isOpen={isCashSectionOpen}
            title={cashDenominationsDropdownHeading}
            initiallyOpen={false}
            toggleSection={() => setCashSectionIsOpen(!isCashSectionOpen)}
            customTitle={true}
          >
            <Motion
              variants={{
                open: { opacity: 1 },
                closed: { opacity: 0 },
              }}
              initial={'open'}
            >
              <SolidDivider />
              <Box background={constants.colors.ATHENS_GREY}>
                <FormInputColumn>
                  <LineItem description="Denomination" amount="Amount" />
                  <DynamicDenominationsFieldGroup
                    denominationsFormState={denominationsFormState}
                    setDenominationsFormState={setDenominationsFormState}
                  />
                </FormInputColumn>
              </Box>
            </Motion>
          </CollapsibleSection>
        </Box>
        <SolidDivider />
        <Box padding="1.5rem">
          <Cluster justify="space-between">
            <Detail as="h5">Checks</Detail>
            <Detail>{formatPrice(totalCheck)}</Detail>
          </Cluster>
          <Cluster justify="space-between" margin="0.5rem 0 0 0">
            <Detail
              as="p"
              variant="small"
              weight={constants.fontWeights.FONT_WEIGHT_REGULAR}
            >
              Physical Checks
            </Detail>
            <Detail
              variant="small"
              weight={constants.fontWeights.FONT_WEIGHT_REGULAR}
            >
              {formatPrice(totalProcessedExternallyCheck)}
            </Detail>
          </Cluster>
          <Cluster justify="space-between" margin="0.25rem 0 0 0">
            <Detail
              as="p"
              variant="small"
              weight={constants.fontWeights.FONT_WEIGHT_REGULAR}
            >
              Check / ICL Checks
            </Detail>
            <Detail
              variant="small"
              weight={constants.fontWeights.FONT_WEIGHT_REGULAR}
            >
              {formatPrice(totalProcessedInternallyCheck)}
            </Detail>
          </Cluster>
        </Box>
        <SolidDivider />
        <Box padding="1.5rem">
          <Cluster justify="space-between">
            <Detail as="h5">Credit</Detail>
            <Detail>{formatPrice(totalCard)}</Detail>
          </Cluster>
        </Box>
        <SolidDivider />
        <Box padding="1.5rem">
          <Cluster justify="space-between">
            <Detail as="h5">Wire Transfer</Detail>
            <Detail>{formatPrice(totalWirePayment)}</Detail>
          </Cluster>
        </Box>
        <SolidDivider />
        <Box padding="1.5rem">
          <Cluster justify="space-between">
            <Detail
              variant="large"
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
            >
              Total
            </Detail>
            <Detail variant="large" data-testid="total">
              {formatPrice(closeoutTotal)}
            </Detail>
          </Cluster>
        </Box>
      </BoxWithShadow>
      <Box padding="2rem 0">
        <BoxWithShadow
          variant="baseStandard"
          borderRadius=".25rem"
          background={constants.colors.WHITE}
        >
          <Checkbox
            id={'confirm-amounts'}
            title={
              <Text weight={constants.fontWeights.FONT_WEIGHT_BOLD}>
                Confirm Amounts
              </Text>
            }
            name={'Confirm Amounts'}
            checked={confirmAmountsBoolean}
            onChange={handleConfirmAmountsCheckboxClick}
          />
          <Detail margin=".5rem 2.5rem 0" as="p">
            By checking the box above, I confirm the amounts above are accurate.
          </Detail>
        </BoxWithShadow>
        {errors &&
          Boolean(
            R.pathOr(false, ['confirmAmounts', 'errors', 'length'], fields),
          ) && (
            <Detail
              variant="small"
              margin=".5rem 0 0"
              color={constants.colors.ERROR_COLOR}
            >
              You must check confirm amounts above to continue
            </Detail>
          )}
      </Box>
      <Cluster justify="space-between">
        <ButtonWithLink
          text="Cancel"
          isLoading={false}
          variant="secondary"
          url={'/pos'}
          textExtraStyles="font-size: 1rem;"
        />
        <Cluster align="center">
          <ButtonWithAction
            text="Open drawer"
            isLoading={false}
            variant="secondary"
            action={() => dispatch(openCashDrawer({ location }))}
            textExtraStyles="font-size: 1rem;"
          />
          <ButtonWithAction
            id="submit-closeout-btn"
            disabled={
              isLoading || (order_items ? order_items.length > 0 : false)
            }
            isLoading={isLoading}
            text="Closeout"
            data-testid="closeout-cta"
            action={handleCloseoutSubmitClick}
            textExtraStyles="font-size: 1rem;"
          />
        </Cluster>
      </Cluster>
    </Box>
  );
};

CloseoutForm.reducer = reducer;
CloseoutForm.mapStateToProps = mapStateToProps;
CloseoutForm.mapDispatchToProps = mapDispatchToProps;
